import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Monitor,
  Megaphone,
  ImageIcon,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  Eye,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#10b981', '#ef4444', '#f59e0b'];

export default function DashboardPage() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
    seedData();
  }, []);

  const seedData = async () => {
    try {
      await api.post('/seed');
    } catch (error) {
      // Seed may fail if data exists, which is fine
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await api.get('/analytics');
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const screenStatusData = analytics?.screen_status ? [
    { name: 'Online', value: analytics.screen_status.online, color: '#10b981' },
    { name: 'Offline', value: analytics.screen_status.offline, color: '#ef4444' },
    { name: 'Maintenance', value: analytics.screen_status.maintenance, color: '#f59e0b' },
  ] : [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-zinc-400 mt-1">Here's what's happening with your screens today</p>
        </div>
        <Link to="/screens">
          <Button className="bg-blue-600 hover:bg-blue-500 btn-glow" data-testid="view-screens-btn">
            View all screens
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Monitor className="w-6 h-6 text-blue-500" />
              </div>
              <Badge variant="outline" className="status-online">
                <Activity className="w-3 h-3 mr-1" />
                {analytics?.online_screens || 0} active
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {analytics?.total_screens || 0}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Total Screens</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Megaphone className="w-6 h-6 text-purple-500" />
              </div>
              <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                {analytics?.active_campaigns || 0} active
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {analytics?.total_campaigns || 0}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Total Campaigns</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <Eye className="w-6 h-6 text-emerald-500" />
              </div>
              <div className="flex items-center text-emerald-500 text-sm">
                <TrendingUp className="w-4 h-4 mr-1" />
                12%
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {(analytics?.total_impressions || 0).toLocaleString()}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Total Impressions</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800 card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-amber-500" />
              </div>
              {analytics?.pending_invoices > 0 && (
                <Badge variant="outline" className="status-maintenance">
                  ${analytics.pending_invoices.toLocaleString()} pending
                </Badge>
              )}
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                ${(analytics?.total_revenue || 0).toLocaleString()}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Total Revenue</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Impressions Chart */}
        <Card className="glass-card border-zinc-800 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Daily Impressions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.daily_impressions || []}>
                  <defs>
                    <linearGradient id="impressionsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#71717a" 
                    fontSize={12}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                  />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#a1a1aa' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="impressions"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#impressionsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Screen Status Chart */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Screen Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={screenStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {screenStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {screenStatusData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-zinc-400">{item.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Top Screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/campaigns" className="block">
              <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Megaphone className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Create Campaign</p>
                    <p className="text-sm text-zinc-500">Launch a new advertising campaign</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-500" />
              </div>
            </Link>
            <Link to="/content" className="block">
              <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <ImageIcon className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Upload Content</p>
                    <p className="text-sm text-zinc-500">Add new media to your library</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-500" />
              </div>
            </Link>
            <Link to="/schedule" className="block">
              <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Schedule Content</p>
                    <p className="text-sm text-zinc-500">Plan your screen displays</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-zinc-500" />
              </div>
            </Link>
          </CardContent>
        </Card>

        {/* Top Screens */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Top Performing Screens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics?.top_screens?.slice(0, 4).map((screen, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center text-sm font-medium text-zinc-400">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-white font-medium">{screen.name}</p>
                      <p className="text-sm text-zinc-500">{screen.uptime_percentage}% uptime</p>
                    </div>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                </div>
              ))}
              {(!analytics?.top_screens || analytics.top_screens.length === 0) && (
                <div className="text-center py-8">
                  <Monitor className="w-12 h-12 mx-auto text-zinc-700 mb-3" />
                  <p className="text-zinc-500">No screens available yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
